import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { isCnmcRegistryRow } from "@/dashboard/inventory_management/send/cnmc/cnmcOutboundRules";
import { cnmcOutboundApi } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcOutboundApi";
import type { CnmcHoldingInfo } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
import type { FlattenedPortfolioItem } from "@/types";
import { computed, readonly, ref, shallowRef, watch, type Ref } from "vue";

/**
 * @property companyId - The company whose CNMC holdings are annotated
 * @property items - The portfolio rows Send lists, as the items cache holds them; a new array (a
 *   refetch after a filing or a sync) fetches the annotations again
 */
export interface UseCnmcHoldingsOptions {
  companyId: () => number;
  items: () => readonly FlattenedPortfolioItem[];
}

/**
 * @property byItemId - Each live CNMC holding row's annotation, by portfolio item id
 * @property ready - False only while the rows hold a Spanish row and the company's first annotation
 *   fetch has not settled yet; Send keeps its selection away from the rows until then. Once it has been
 *   true over a non-empty set of rows it stays true for the company
 */
export interface CnmcHoldings {
  byItemId: Readonly<Ref<ReadonlyMap<number, CnmcHoldingInfo>>>;
  ready: Readonly<Ref<boolean>>;
}

/**
 * The CNMC annotations Send files the company's Spanish rows with. Only fetched while the rows hold a
 * Spanish (`ES`) row, and again on every new array of rows; a refetch keeps the previous annotations
 * until it answers. A failed fetch goes to the global error handler and still settles `ready`, so
 * Send never waits for good.
 *
 * `ready` is computed from the current rows rather than latched at mount: the cache is empty on mount,
 * so a latch would let the selection restore a URL selection before any Spanish row is annotated. It
 * latches only once it has been true over rows, after the selection hydrated from them: a refetch that
 * brings in the company's first Spanish row must not empty Send's rows, which would drop the
 * selection; that row stays unsendable until its annotation arrives.
 */
export function useCnmcHoldings(options: UseCnmcHoldingsOptions): CnmcHoldings {
  const { companyId, items } = options;
  const { globalErrorHandler } = useGlobalHandlers();

  const byItemId = shallowRef<ReadonlyMap<number, CnmcHoldingInfo>>(new Map());
  // The company whose first fetch has settled; null until then.
  const settledFor = ref<number | null>(null);
  // The company Send has shown rows to; null until then.
  const shownFor = ref<number | null>(null);
  let latestRequest = 0;

  const hasSpanishRows = computed(() => items().some(isCnmcRegistryRow));
  const annotated = computed(
    () => !hasSpanishRows.value || settledFor.value === companyId()
  );
  const ready = computed(
    () => shownFor.value === companyId() || annotated.value
  );

  const fetchHoldings = async (company: number) => {
    const request = ++latestRequest;
    try {
      const { data } = await cnmcOutboundApi.holdings(company);
      if (request === latestRequest)
        byItemId.value = new Map(
          data.holdings.map((holding) => [holding.portfolio_item_id, holding])
        );
    } catch (error) {
      if (request === latestRequest) globalErrorHandler(error);
    } finally {
      if (request === latestRequest) settledFor.value = company;
    }
  };

  watch(
    [companyId, items],
    ([company, rows], previous) => {
      if (previous && previous[0] !== company) {
        // Another company's annotations never describe these rows.
        latestRequest++;
        byItemId.value = new Map();
        settledFor.value = null;
        shownFor.value = null;
      }
      if (rows.some(isCnmcRegistryRow)) void fetchHoldings(company);
    },
    { immediate: true }
  );
  watch(
    [annotated, () => items().length],
    ([isAnnotated, count]) => {
      if (isAnnotated && count > 0) shownFor.value = companyId();
    },
    { immediate: true }
  );

  return { byItemId: readonly(byItemId), ready };
}
